import axios from 'axios';
import { clearAuthData } from './auth';
import tokenManager from './tokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  async config => {
    try {
      if (
        config.url?.includes('/auth/login') ||
        config.url?.includes('/auth/register') ||
        config.url?.includes('/auth/refresh')
      ) {
        return config;
      }

      const token = await tokenManager.ensureValidToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      clearAuthData();
      tokenManager.clearTokens();
      window.location.href = '/login';
      return Promise.reject(new Error('Authentication failed'));
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/register') ||
        originalRequest.url?.includes('/auth/refresh')
      ) {
        return Promise.reject(error);
      }

      try {
        const newToken = await tokenManager.refreshTokens();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        clearAuthData();
        tokenManager.clearTokens();

        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: credentials => api.post('/auth/login', credentials),
  register: userData => api.post('/auth/register', userData),
  forgotPassword: email => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post(`/auth/reset-password/${token}`, { password }),
  dismissWelcome: () => api.put('/auth/dismiss-welcome'),
  refreshToken: refreshToken => api.post('/auth/refresh', { refreshToken }),
  logout: () => api.post('/auth/logout'),
  logoutAll: () => api.post('/auth/logout-all'),
  verifyToken: token => api.post('/auth/verify-token', { token }),
};

export const twoFactorAPI = {
  setup: () => api.post('/auth/2fa/setup'),
  verifySetup: token => api.post('/auth/2fa/verify-setup', { token }),
  disable: (password, token, backupCode) =>
    api.post('/auth/2fa/disable', { password, token, backupCode }),
  getStatus: () => api.get('/auth/2fa/status'),
  generateBackupCodes: password => api.post('/auth/2fa/backup-codes', { password }),
  getTrustedDevices: () => api.get('/auth/2fa/trusted-devices'),
  removeTrustedDevice: deviceId => api.delete(`/auth/2fa/trusted-devices/${deviceId}`),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: id => api.get(`/users/${id}`),
  create: userData => api.post('/users', userData),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  delete: id => api.delete(`/users/${id}`),
  resetPassword: (id, newPassword) => api.put(`/users/${id}/reset-password`, { newPassword }),
  updateCompanyStatus: (id, companyStatus) =>
    api.put(`/users/${id}/company-status`, { companyStatus }),
  uploadAvatar: formData =>
    api.post('/users/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

export const customersAPI = {
  getAll: (params = {}) => api.get('/customers', { params }),
  getById: id => api.get(`/customers/${id}`),
  create: customerData => api.post('/customers', customerData),
  update: (id, customerData) => api.put(`/customers/${id}`, customerData),
  delete: id => api.delete(`/customers/${id}`),
  search: (query, params = {}) => api.get(`/customers/search/${query}`, { params }),
  syncFromCRM: () => api.post('/customers/sync-crm'),
};

export const crmAPI = {
  getSettings: () => api.get('/crm/settings'),
  updateSettings: settings => api.put('/crm/settings', settings),
  testConnection: settings => api.post('/crm/test-connection', settings),
  syncCustomers: () => api.post('/crm/sync-customers'),
  searchCustomers: searchTerm => api.get(`/crm/search-customers/${encodeURIComponent(searchTerm)}`),
  importCustomer: customerData => api.post('/crm/import-customer', customerData),
};

export const businessTypesAPI = {
  getAll: (includeArchived = false) => api.get('/business-types', { params: { includeArchived } }),
  getById: id => api.get(`/business-types/${id}`),
  create: businessTypeData => api.post('/business-types', businessTypeData),
  update: (id, businessTypeData) => api.put(`/business-types/${id}`, businessTypeData),
  delete: id => api.delete(`/business-types/${id}`),
  archive: (id, archived) => api.patch(`/business-types/${id}/archive`, { archived }),
  search: (query, includeArchived = false) =>
    api.get(`/business-types/search/${query}`, { params: { includeArchived } }),
  importCSV: formData => {
    return axios
      .create({
        baseURL: API_BASE_URL,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      .post('/business-types/import', formData);
  },
};

export const magazinesAPI = {
  getAll: (includeArchived = false) => api.get('/magazines', { params: { includeArchived } }),
  getById: id => api.get(`/magazines/${id}`),
  create: magazineData => api.post('/magazines', magazineData),
  update: (id, magazineData) => api.put(`/magazines/${id}`, magazineData),
  delete: id => api.delete(`/magazines/${id}`),
  archive: (id, archived) => api.patch(`/magazines/${id}/archive`, { archived }),
  getCurrentIssue: magazineId => api.get(`/magazines/current-issue/${magazineId}`),
  toggleIssue: (id, issueName, hidden) =>
    api.patch(`/magazines/${id}/toggle-issue`, { issueName, hidden }),
};

export const schedulesAPI = {
  getAll: (includeDisabled = false) => api.get(`/schedules?includeDisabled=${includeDisabled}`),
  getById: id => api.get(`/schedules/${id}`),
  create: scheduleData => api.post('/schedules', scheduleData),
  update: (id, scheduleData) => api.put(`/schedules/${id}`, scheduleData),
  delete: id => api.delete(`/schedules/${id}`),
  archive: id => api.patch(`/schedules/${id}/archive`),
  disable: id => api.patch(`/schedules/${id}/disable`),
  getAvailableIssues: scheduleId => api.get(`/schedules/${scheduleId}/available-issues`),
  validateIssue: issueName => api.get(`/schedules/validate-issue/${encodeURIComponent(issueName)}`),
};

export const adSizesAPI = {
  getAll: () => api.get('/ad-sizes'),
  initialize: () => api.post('/ad-sizes/initialize'),
  update: (id, data) => api.patch(`/ad-sizes/${id}`, data),
};

export const contentSizesAPI = {
  getAll: (includeArchived = false) => api.get('/content-sizes', { params: { includeArchived } }),
  getById: id => api.get(`/content-sizes/${id}`),
  create: contentSizeData => api.post('/content-sizes', contentSizeData),
  update: (id, contentSizeData) => api.put(`/content-sizes/${id}`, contentSizeData),
  delete: id => api.delete(`/content-sizes/${id}`),
  archive: (id, archived) => api.patch(`/content-sizes/${id}/archive`, { archived }),
  getPrice: (contentSizeId, magazineId) =>
    api.get(`/content-sizes/${contentSizeId}/price/${magazineId}`),
  cleanup: () => api.post('/content-sizes/cleanup'),
};

export const bookingsAPI = {
  getAll: (params = {}) => api.get('/bookings', { params }),
  getById: id => api.get(`/bookings/${id}`),
  getRedirect: id => api.get(`/bookings/${id}/redirect`),
  create: bookingData => api.post('/bookings', bookingData),
  update: (id, bookingData) => api.put(`/bookings/${id}`, bookingData),
  sync: payload => api.post('/bookings/sync', payload),
  delete: id => api.delete(`/bookings/${id}`),
  forCustomer: customerId => api.get(`/bookings/customer/${customerId}`),
  getReportData: (params = {}) => api.get('/bookings/report/data', { params }),
  getCurrentIssue: () => api.get('/bookings/current-issue'),
};

export const artworkSchedulesAPI = {
  forBooking: bookingId => api.get(`/artwork-schedules/booking/${bookingId}`),
  forCustomer: customerId => api.get(`/artwork-schedules/customer/${customerId}`),
  updateForBooking: (bookingId, payload) =>
    api.put(`/artwork-schedules/booking/${bookingId}`, payload),
  getTBC: () => api.get('/artwork-schedules/tbc'),
  bulkUpdate: updates => api.put('/artwork-schedules/bulk', { updates }),
};

export const leafletDeliveryAPI = {
  getAll: (params = {}) => api.get('/leaflet-delivery', { params }),
  getById: id => api.get(`/leaflet-delivery/${id}`),
  create: deliveryData => api.post('/leaflet-delivery', deliveryData),
  update: (id, deliveryData) => api.put(`/leaflet-delivery/${id}`, deliveryData),
  delete: id => api.delete(`/leaflet-delivery/${id}`),
  getReportData: (params = {}) => api.get('/leaflet-delivery/report/data', { params }),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getCurrentIssue: magazineId => api.get(`/dashboard/current-issue/${magazineId}`),
  getPublications: () => api.get('/dashboard/publications'),
  getPublicationsAllTime: () => api.get('/dashboard/publications/all-time'),
  getTopCustomers: () => api.get('/dashboard/top-customers'),
  getRecentActivity: () => api.get('/dashboard/recent-activity'),
};

export const backupAPI = {
  list: () => api.get('/backup/list'),
  create: password => api.post('/backup/create', { password }),
  restore: (password, filename) => api.post('/backup/restore', { password, filename }),
  download: filename => api.get(`/backup/download/${filename}`, { responseType: 'blob' }),
  upload: (password, backupData) => api.post('/backup/upload', { password, backupData }),
  delete: (filename, password) => api.delete(`/backup/${filename}`, { data: { password } }),
};

export default api;
